import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { getFacebookMessagesDetailWithMeta } from "@api/services/facebook";
import { getZaloMessagesDetailWithMeta } from "@api/services/zalo";
import type { Message } from "@interfaces/chat.interface";
import type { GetFacebookMessagesParams } from "@api/services/facebook";
import type { GetZaloMessagesParams } from "@api/services/zalo";

export interface ConversationMeta {
  offset: number;
  total: number;
  limit: number;
}

export interface MessagesState {
  messagesByConversation: Record<string, Message[]>;
  loading: Record<string, boolean>;
  error: Record<string, string | null>;
  meta: Record<string, ConversationMeta>;
}

const initialState: MessagesState = {
  messagesByConversation: {},
  loading: {},
  error: {},
  meta: {},
};

export const fetchFacebookMessagesDetail = createAsyncThunk(
  "messages/fetchFacebookMessagesDetail",
  async (params: GetFacebookMessagesParams, { rejectWithValue, signal }) => {
    try {
      const result = await getFacebookMessagesDetailWithMeta({
        ...params,
        signal,
      });
      return {
        conversationId: params.conversationId,
        messages: result.messages,
        offset: result.offset,
        total: result.total,
        limit: result.limit,
        isLoadMore: (params.offset || 0) > 0,
      };
    } catch (error) {
      return rejectWithValue({
        conversationId: params.conversationId,
        error:
          error instanceof Error
            ? error.message
            : "Failed to fetch Facebook messages detail",
      });
    }
  }
);

export const fetchZaloMessagesDetail = createAsyncThunk(
  "messages/fetchZaloMessagesDetail",
  async (params: GetZaloMessagesParams, { rejectWithValue, signal }) => {
    try {
      const result = await getZaloMessagesDetailWithMeta({
        ...params,
        signal,
      });
      return {
        conversationId: params.conversationId,
        messages: result.messages,
        offset: result.offset,
        total: result.total,
        limit: result.limit,
        isLoadMore: (params.offset || 0) > 0,
      };
    } catch (error) {
      return rejectWithValue({
        conversationId: params.conversationId,
        error:
          error instanceof Error
            ? error.message
            : "Failed to fetch Zalo messages detail",
      });
    }
  }
);

const messagesSlice = createSlice({
  name: "messages",
  initialState,
  reducers: {
    clearMessages: (state, action: PayloadAction<string>) => {
      const conversationId = action.payload;
      delete state.messagesByConversation[conversationId];
      delete state.loading[conversationId];
      delete state.error[conversationId];
      delete state.meta[conversationId];
    },
    addMessage: (
      state,
      action: PayloadAction<{ conversationId: string; message: Message }>
    ) => {
      const { conversationId, message } = action.payload;

      if (!state.messagesByConversation[conversationId]) {
        state.messagesByConversation[conversationId] = [];
      }

      let existingIndex = state.messagesByConversation[
        conversationId
      ].findIndex((m) => m.id === message.id);

      if (existingIndex < 0 && message.meta?.direction === "OUTBOUND") {
        const messages = state.messagesByConversation[conversationId];
        const messageTime = new Date(message.createdAt).getTime();

        const recentOptimisticMessages = messages.filter((m) => {
          if (m.meta?.direction !== "OUTBOUND" || !m.id?.startsWith("temp-")) {
            return false;
          }

          const mTime = new Date(m.createdAt).getTime();
          const timeDiff = Math.abs(messageTime - mTime);

          return timeDiff <= 10000;
        });

        if (message.meta?.isTemplate && message.meta?.templateId) {
          const templateId = message.meta.templateId;
          const optimisticIndex = recentOptimisticMessages.findIndex((m) => {
            return (
              m.id?.startsWith("temp-template-") &&
              m.meta?.templateId === templateId &&
              (m.status === "sending" || m.status === "sent")
            );
          });

          if (optimisticIndex >= 0) {
            const foundMessage = recentOptimisticMessages[optimisticIndex];
            existingIndex = messages.findIndex((m) => m.id === foundMessage.id);
          }
        }

        if (existingIndex < 0 && message.meta?.isTemplate) {
          const templateType = message.meta.templateType;
          const templatePayload = message.meta.templatePayload;

          const optimisticIndex = recentOptimisticMessages.findIndex((m) => {
            if (!m.meta?.isTemplate || m.meta?.templateType !== templateType) {
              return false;
            }
            if (templatePayload && m.meta?.templatePayload) {
              try {
                const msgPayload =
                  typeof templatePayload === "string"
                    ? JSON.parse(templatePayload)
                    : templatePayload;
                const mPayload =
                  typeof m.meta.templatePayload === "string"
                    ? JSON.parse(m.meta.templatePayload)
                    : m.meta.templatePayload;
                return (
                  JSON.stringify(msgPayload) === JSON.stringify(mPayload) ||
                  (msgPayload.media_type &&
                    mPayload.media_type &&
                    msgPayload.media_type === mPayload.media_type) ||
                  (msgPayload.title &&
                    mPayload.title &&
                    msgPayload.title === mPayload.title)
                );
              } catch {
                return false;
              }
            }

            return false;
          });

          if (optimisticIndex >= 0) {
            const foundMessage = recentOptimisticMessages[optimisticIndex];
            existingIndex = messages.findIndex((m) => m.id === foundMessage.id);
          }
        }
        if (existingIndex < 0 && message.body) {
          let messageBodyStr = "";
          let messageBodyObj: {
            templateType?: string;
            payload?: unknown;
          } | null = null;

          try {
            if (typeof message.body === "string") {
              messageBodyStr = message.body.trim();
              if (messageBodyStr.startsWith("{")) {
                messageBodyObj = JSON.parse(messageBodyStr) as {
                  templateType?: string;
                  payload?: unknown;
                };
              }
            }
          } catch {
            // Not JSON, use as string
          }

          const optimisticIndex = recentOptimisticMessages.findIndex((m) => {
            if (!m.body) return false;
            let mBodyStr = "";
            let mBodyObj: { templateType?: string; payload?: unknown } | null =
              null;

            try {
              if (typeof m.body === "string") {
                mBodyStr = m.body.trim();
                if (mBodyStr.startsWith("{")) {
                  mBodyObj = JSON.parse(mBodyStr) as {
                    templateType?: string;
                    payload?: unknown;
                  };
                }
              }
            } catch {
              // Not JSON, use as string
            }

            if (messageBodyObj && mBodyObj) {
              return (
                messageBodyObj.templateType === mBodyObj.templateType &&
                JSON.stringify(messageBodyObj.payload) ===
                  JSON.stringify(mBodyObj.payload)
              );
            }

            if (messageBodyStr && mBodyStr) {
              return (
                messageBodyStr === mBodyStr ||
                messageBodyStr.includes(mBodyStr) ||
                mBodyStr.includes(messageBodyStr)
              );
            }

            return false;
          });

          if (optimisticIndex >= 0) {
            const foundMessage = recentOptimisticMessages[optimisticIndex];
            existingIndex = messages.findIndex((m) => m.id === foundMessage.id);
          }
        }
      }

      if (existingIndex >= 0) {
        const existingMessage =
          state.messagesByConversation[conversationId][existingIndex];

        const hasBlobUrl = existingMessage.body?.startsWith("blob:");
        const hasPreviewInMeta = !!existingMessage.meta?.previewUrl;
        const existingPreviewUrl = hasBlobUrl
          ? existingMessage.body
          : existingMessage.meta?.previewUrl || null;

        const newMessageHasHttpUrl =
          message.body?.startsWith("http") &&
          !message.body?.startsWith("blob:");
        const newMessageHasBlobUrl = message.body?.startsWith("blob:");

        if (hasBlobUrl || hasPreviewInMeta) {
          if (newMessageHasHttpUrl && message.type === existingMessage.type) {
            state.messagesByConversation[conversationId][existingIndex] = {
              ...message,
              body: message.body,
              meta: {
                ...existingMessage.meta,
                ...message.meta,
                previewUrl: existingPreviewUrl as string,
              },
            };
          } else {
            state.messagesByConversation[conversationId][existingIndex] = {
              ...message,
              body: existingMessage.body || (existingPreviewUrl as string),
              meta: {
                ...existingMessage.meta,
                ...message.meta,
              },
            };
          }
        } else if (newMessageHasHttpUrl || newMessageHasBlobUrl) {
          state.messagesByConversation[conversationId][existingIndex] = message;
        } else {
          state.messagesByConversation[conversationId][existingIndex] = message;
        }
      } else {
        state.messagesByConversation[conversationId].push(message);

        if (
          message.meta?.direction === "OUTBOUND" &&
          !message.id?.startsWith("temp-")
        ) {
          const messages = state.messagesByConversation[conversationId];
          const messageTime = new Date(message.createdAt).getTime();
          const duplicatesToRemove: number[] = [];

          messages.forEach((m, index) => {
            if (m.id === message.id) return;
            if (!m.id?.startsWith("temp-") || m.meta?.direction !== "OUTBOUND")
              return;

            const mTime = new Date(m.createdAt).getTime();
            const timeDiff = Math.abs(messageTime - mTime);
            if (timeDiff > 10000) return;
            let isDuplicate = false;

            if (message.meta?.isTemplate && m.meta?.isTemplate) {
              if (message.meta?.templateId && m.meta?.templateId) {
                isDuplicate = message.meta.templateId === m.meta.templateId;
              } else if (message.meta?.templateType && m.meta?.templateType) {
                isDuplicate = message.meta.templateType === m.meta.templateType;
              }
            }

            if (!isDuplicate && message.body && m.body) {
              const msgBody =
                typeof message.body === "string" ? message.body.trim() : "";
              const mBody = typeof m.body === "string" ? m.body.trim() : "";

              if (msgBody && mBody) {
                if (msgBody.startsWith("{") && mBody.startsWith("{")) {
                  try {
                    const msgObj = JSON.parse(msgBody);
                    const mObj = JSON.parse(mBody);
                    if (msgObj.templateType && mObj.templateType) {
                      isDuplicate =
                        msgObj.templateType === mObj.templateType &&
                        JSON.stringify(msgObj.payload) ===
                          JSON.stringify(mObj.payload);
                    }
                  } catch {
                    isDuplicate = msgBody === mBody;
                  }
                } else {
                  isDuplicate =
                    msgBody === mBody ||
                    msgBody.includes(mBody) ||
                    mBody.includes(msgBody);
                }
              }
            }

            if (isDuplicate) {
              duplicatesToRemove.push(index);
            }
          });

          duplicatesToRemove.reverse().forEach((index) => {
            state.messagesByConversation[conversationId].splice(index, 1);
          });
        }
      }
    },
    updateMessageStatus: (
      state,
      action: PayloadAction<{
        conversationId: string;
        messageId: string;
        status: Message["status"];
      }>
    ) => {
      const { conversationId, messageId, status } = action.payload;
      const messages = state.messagesByConversation[conversationId];
      if (messages) {
        const messageIndex = messages.findIndex((m) => m.id === messageId);
        if (messageIndex >= 0) {
          messages[messageIndex].status = status;
        }
      }
    },
    removeMessage: (
      state,
      action: PayloadAction<{ conversationId: string; messageId: string }>
    ) => {
      const { conversationId, messageId } = action.payload;
      const messages = state.messagesByConversation[conversationId];
      if (messages) {
        state.messagesByConversation[conversationId] = messages.filter(
          (m) => m.id !== messageId
        );
      }
    },
    updateMessageUrl: (
      state,
      action: PayloadAction<{
        conversationId: string;
        messageId: string;
        newUrl: string;
      }>
    ) => {
      const { conversationId, messageId, newUrl } = action.payload;
      const messages = state.messagesByConversation[conversationId];
      if (messages) {
        const messageIndex = messages.findIndex((m) => m.id === messageId);
        if (messageIndex >= 0) {
          const message = messages[messageIndex];
          // Update body if it's an image/video, otherwise update meta.previewUrl
          if (message.type === "image" || message.type === "video") {
            messages[messageIndex] = {
              ...message,
              body: newUrl,
              meta: {
                ...message.meta,
                previewUrl: newUrl,
              },
            };
          } else {
            messages[messageIndex] = {
              ...message,
              meta: {
                ...message.meta,
                previewUrl: newUrl,
              },
            };
          }
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFacebookMessagesDetail.pending, (state, action) => {
        const conversationId = action.meta.arg.conversationId;
        state.loading[conversationId] = true;
        state.error[conversationId] = null;
      })
      .addCase(fetchFacebookMessagesDetail.fulfilled, (state, action) => {
        const { conversationId, messages, offset, total, limit, isLoadMore } =
          action.payload;
        state.loading[conversationId] = false;

        if (isLoadMore) {
          const existingMessages =
            state.messagesByConversation[conversationId] || [];
          const existingIds = new Set(existingMessages.map((m) => m.id));
          const newMessages = messages.filter((m) => !existingIds.has(m.id));
          state.messagesByConversation[conversationId] = [
            ...newMessages,
            ...existingMessages,
          ];
        } else {
          const existingMessages =
            state.messagesByConversation[conversationId] || [];
          const existingMap = new Map(existingMessages.map((m) => [m.id, m]));

          const mergedMessages = messages.map((serverMessage) => {
            const existing = existingMap.get(serverMessage.id);

            if (existing) {
              const hasBlobUrl = existing.body?.startsWith("blob:");
              const serverHasHttpUrl =
                serverMessage.body?.startsWith("http") &&
                !serverMessage.body?.startsWith("blob:");

              if (hasBlobUrl && serverHasHttpUrl) {
                return {
                  ...serverMessage,
                  meta: {
                    ...existing.meta,
                    ...serverMessage.meta,
                    previewUrl: existing.body as string,
                  },
                };
              } else if (
                hasBlobUrl &&
                (!serverMessage.body || serverMessage.body.trim() === "")
              ) {
                return {
                  ...serverMessage,
                  body: existing.body as string,
                  meta: {
                    ...existing.meta,
                    ...serverMessage.meta,
                  },
                };
              }
            }

            return serverMessage;
          });

          state.messagesByConversation[conversationId] = mergedMessages;
        }

        state.meta[conversationId] = {
          offset,
          total,
          limit,
        };
        state.error[conversationId] = null;
      })
      .addCase(fetchFacebookMessagesDetail.rejected, (state, action) => {
        const payload = action.payload as {
          conversationId: string;
          error: string;
        };
        if (payload) {
          state.loading[payload.conversationId] = false;
          state.error[payload.conversationId] = payload.error;
        }
      })
      .addCase(fetchZaloMessagesDetail.pending, (state, action) => {
        const conversationId = action.meta.arg.conversationId;
        state.loading[conversationId] = true;
        state.error[conversationId] = null;
      })
      .addCase(fetchZaloMessagesDetail.fulfilled, (state, action) => {
        const { conversationId, messages, offset, total, limit, isLoadMore } =
          action.payload;
        state.loading[conversationId] = false;

        if (isLoadMore) {
          const existingMessages =
            state.messagesByConversation[conversationId] || [];
          const existingIds = new Set(existingMessages.map((m) => m.id));
          const newMessages = messages.filter((m) => !existingIds.has(m.id));
          state.messagesByConversation[conversationId] = [
            ...newMessages,
            ...existingMessages,
          ];
        } else {
          const existingMessages =
            state.messagesByConversation[conversationId] || [];
          const existingMap = new Map(existingMessages.map((m) => [m.id, m]));

          const mergedMessages = messages.map((serverMessage) => {
            const existing = existingMap.get(serverMessage.id);

            if (existing) {
              const hasBlobUrl = existing.body?.startsWith("blob:");
              const serverHasHttpUrl =
                serverMessage.body?.startsWith("http") &&
                !serverMessage.body?.startsWith("blob:");

              if (hasBlobUrl && serverHasHttpUrl) {
                return {
                  ...serverMessage,
                  meta: {
                    ...existing.meta,
                    ...serverMessage.meta,
                    previewUrl: existing.body as string,
                  },
                };
              } else if (
                hasBlobUrl &&
                (!serverMessage.body || serverMessage.body.trim() === "")
              ) {
                return {
                  ...serverMessage,
                  body: existing.body as string,
                  meta: {
                    ...existing.meta,
                    ...serverMessage.meta,
                  },
                };
              }
            }

            return serverMessage;
          });

          state.messagesByConversation[conversationId] = mergedMessages;
        }

        state.meta[conversationId] = {
          offset,
          total,
          limit,
        };
        state.error[conversationId] = null;
      })
      .addCase(fetchZaloMessagesDetail.rejected, (state, action) => {
        const payload = action.payload as {
          conversationId: string;
          error: string;
        };
        if (payload) {
          state.loading[payload.conversationId] = false;
          state.error[payload.conversationId] = payload.error;
        }
      });
  },
});

export const {
  clearMessages,
  addMessage,
  updateMessageStatus,
  removeMessage,
  updateMessageUrl,
} = messagesSlice.actions;
export default messagesSlice.reducer;
